```vue
<template>
  <Toast ref="toast" />

  <div class="p-4">
    <!-- Фильтры -->
    <div class="filters" v-if="!dashboard">
      <div class="filter-block">
        <label>Надстройка (Prefix)</label>

        <MultiSelect
          v-model="filters.prefixDefinitionIds"
          :options="prefixOptions"
          optionLabel="label"
          optionValue="value"
          display="chip"
          filter
        />

        <div class="quality-buttons">
          <button
            v-for="q in qualityOptions"
            :key="'p-' + q.value"
            :style="{ backgroundColor: q.color }"
            :class="{ active: filters.prefixQualities.includes(q.value) }"
            @click="toggleQuality('prefixQualities', q.value)"
          />
        </div>
      </div>

      <div class="filter-block">
        <label>Отклонение (Suffix)</label>

        <MultiSelect
          v-model="filters.suffixDefinitionIds"
          :options="suffixOptions"
          optionLabel="label"
          optionValue="value"
          display="chip"
          filter
        />

        <div class="quality-buttons">
          <button
            v-for="q in qualityOptions"
            :key="'s-' + q.value"
            :style="{ backgroundColor: q.color }"
            :class="{ active: filters.suffixQualities.includes(q.value) }"
            @click="toggleQuality('suffixQualities', q.value)"
          />
        </div>
      </div>

      <div class="filter-block">
        <label>Концепт (Affix)</label>

        <MultiSelect
          v-model="filters.affixDefinitionIds"
          :options="affixOptions"
          optionLabel="label"
          optionValue="value"
          display="chip"
          filter
        />

        <div class="quality-buttons">
          <button
            v-for="q in qualityOptions"
            :key="'a-' + q.value"
            :style="{ backgroundColor: q.color, color: q.color }"
            :class="{ active: filters.affixQualities.includes(q.value) }"
            @click="toggleQuality('affixQualities', q.value)"
          />
        </div>
      </div>

      <div class="filter-block">
        <label>Быстрый поиск по названиям</label>
        <div class="search-input-group">
          <AutoComplete
            v-model="searchQuery"
            :suggestions="filteredSuggestions"
            optionLabel="name"
            placeholder="Введите название атрибута..."
            @complete="searchAttributes"
            @select="handleAttributeSelect"
            class="search-autocomplete"
            :dropdown="true"
            :minLength="2"
          />
          <button
            v-if="searchQuery"
            @click="clearSearch"
            class="clear-button"
            v-tooltip="'Очистить поиск'"
          >
            ✕
          </button>
        </div>
      </div>
    </div>

    <DataTable
      :value="filteredRows"
      stripedRows
      sortMode="multiple"
      :multiSortMeta="multiSortMeta"
      responsiveLayout="scroll"
      :paginator="true"
      :rows="25"
      :rowsPerPageOptions="[10, 25, 50, 100]"
      currentPageReportTemplate="Показано {first} - {last} из {totalRecords}"
      class="table"
    >
      <Column header="Модули" style="min-width: 300px">
        <template #body="slotProps">
          <div
            class="attrs"
            @click="
              copyToClipboard(
                `${slotProps.data.prefix.name} ${slotProps.data.suffix.name} ${slotProps.data.affix.name}`
              )
            "
          >
            <span :style="`color: ${getQuality(slotProps.data.prefix.quality)?.color}`">
              {{ slotProps.data.prefix.name }}
            </span>

            <span :style="`color: ${getQuality(slotProps.data.suffix.quality)?.color}`">
              {{ slotProps.data.suffix.name }}
            </span>

            <span :style="`color: ${getQuality(slotProps.data.affix.quality)?.color}`">
              {{ slotProps.data.affix.name }}
            </span>
          </div>
        </template>
      </Column>

      <Column header="Выкуп" sortable field="buyoutPrice">
        <template #body="slotProps">
          {{ formatPrice(slotProps.data.buyoutPrice) }}
        </template>
      </Column>

      <Column header="Цена продажи" sortable field="marketPrice">
        <template #body="slotProps">
          <div>
            <div
              @click="copyToClipboard(Math.round(slotProps.data.marketPrice).toString(), 'price')"
              class="clickable-price"
            >
              {{ formatPrice(Math.round(slotProps.data.marketPrice)) }}
            </div>
          </div>
        </template>
      </Column>

      <Column header="Первый атрибут" sortable>
        <template #body="slotProps">
          <div
            :style="{
              color:
                slotProps.data.AC?.avgPrice < slotProps.data?.marketPrice
                  ? '#ff6b6b'
                  : slotProps.data.AC?.avgPrice > slotProps.data?.marketPrice
                  ? '#51cf66'
                  : 'inherit'
            }"
          >
            {{ formatPrice(slotProps.data.AC?.avgPrice) }}
          </div>
          <div style="font-size: 12px; color: gray">
            {{ getParameterRu(slotProps.data.prefix.definitionId, 'prefix') }}
          </div>
          <div v-if="slotProps.data.AC?.salesCount" style="font-size: 12px; color: gray">
            {{ slotProps.data.AC?.salesCount }}
          </div>
        </template>
      </Column>

      <Column header="Второй атрибут" sortable>
        <template #body="slotProps">
          <div
            :style="{
              color:
                slotProps.data.BC?.avgPrice < slotProps.data?.marketPrice
                  ? '#ff6b6b'
                  : slotProps.data.BC?.avgPrice > slotProps.data?.marketPrice
                  ? '#51cf66'
                  : 'inherit'
            }"
          >
            {{ formatPrice(slotProps.data.BC?.avgPrice) }}
          </div>
          <div style="font-size: 12px; color: gray">
            {{ getParameterRu(slotProps.data.suffix.definitionId, 'suffix') }}
          </div>
          <div v-if="slotProps.data.BC?.salesCount" style="font-size: 12px; color: gray">
            {{ slotProps.data.BC?.salesCount }}
          </div>
        </template>
      </Column>

      <Column header="Третий атрибут" sortable field="affixPrice">
        <template #body="slotProps">
          <div>
            <div>{{ formatPrice(slotProps.data.affixPrice) }}</div>
            <div style="font-size: 12px; color: gray">
              {{ getParameterRu(slotProps.data.affix.definitionId, 'affix') }}
            </div>
            <div v-if="slotProps.data.affixSalesCount" style="font-size: 11px; color: #888">
              {{ slotProps.data.affixSalesCount }}
            </div>
          </div>
        </template>
      </Column>

      <Column header="Точное попадание из трех атрибутов" sortable field="price">
        <template #body="slotProps">
          <span
            :style="{
              color:
                slotProps.data?.price < slotProps.data?.marketPrice
                  ? '#ff6b6b'
                  : slotProps.data?.price > slotProps.data?.marketPrice
                  ? '#51cf66'
                  : 'inherit'
            }"
          >
            {{ formatPrice(slotProps.data?.price) }}
          </span>

          <div v-if="slotProps.data.salesCount" style="font-size: 12px; color: gray">
            {{ slotProps.data.salesCount }}
          </div>
        </template>
      </Column>

      <Column header="Профит" sortable field="profit">
        <template #body="slotProps">
          <span
            @click="copyToClipboard(slotProps.data.profit.toString(), 'price')"
            :class="profitClass(slotProps.data.profit)"
            class="clickable-price"
          >
            {{ formatPrice(slotProps.data.profit) }}
          </span>
        </template>
      </Column>

      <Column header="Продаж в день основного атрибута" sortable field="affixLiquidityPerDay">
        <template #body="slotProps">
          {{ slotProps.data.affixLiquidityPerDay?.toFixed(2) }}
        </template>
      </Column>

      <Column header="Очки" sortable field="score">
        <template #body="slotProps">
          {{ slotProps.data.score.toFixed(2) }}
        </template>
      </Column>

      <Column header="История" style="width: 80px; text-align: center">
        <template #body="slotProps">
          <Button
            icon="pi pi-external-link"
            class="p-button-rounded p-button-sm history-button"
            @click="goToModuleHistory(slotProps.data)"
            v-tooltip="'View module history'"
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, ref, reactive } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import Tooltip from 'primevue/tooltip'
import AutoComplete from 'primevue/autocomplete'
import MultiSelect from 'primevue/multiselect'
import { copyText } from 'vue3-clipboard'
import { QUALITY_LIST, MODULE_STATS } from '@/components/module/data'

const props = defineProps<{
  lots: Array<AuctionLot>
  dashboard: boolean
}>()

interface AuctionLot {
  itemId: string
  startPrice: number
  buyoutPrice: number
  additional: any
  salesCount: number
  information: any
  prefix: any
  suffix: any
  affix: any
}

/* сортировка по умолчанию */
const multiSortMeta = ref([{ field: 'score', order: -1 }])

const filteredRows = computed(() => {
  if (props.dashboard) {
    return rows.value.filter(i => i.score > 0 && i.buyoutPrice)
  } else {
    return rows.value
  }
})

/* подготовка строк таблицы с учетом комиссии 5% и фильтрации */
const rows = computed(() => {
  if (!props.lots.length) return []

  let filteredLots = props.lots

  // Фильтрация по атрибутам и качествам
  filteredLots = filteredLots.filter(lot => {
    // Проверяем префиксы
    if (filters.prefixDefinitionIds.length > 0) {
      const prefixId = lot.prefix?.definitionId
        ?.replace(/_(pre|suf|aff)$/g, '')
        .replace(/_(pre|suf|aff)_?/g, '')
      if (!prefixId || !filters.prefixDefinitionIds.includes(prefixId)) return false

      if (filters.prefixQualities.length > 0) {
        if (!filters.prefixQualities.includes(lot.prefix?.quality ?? 0)) return false
      }
    }

    // Проверяем суффиксы
    if (filters.suffixDefinitionIds.length > 0) {
      const suffixId = lot.suffix?.definitionId
        ?.replace(/_(pre|suf|aff)$/g, '')
        .replace(/_(pre|suf|aff)_?/g, '')
      if (!suffixId || !filters.suffixDefinitionIds.includes(suffixId)) return false

      if (filters.suffixQualities.length > 0) {
        if (!filters.suffixQualities.includes(lot.suffix?.quality ?? 0)) return false
      }
    }

    // Проверяем аффиксы
    if (filters.affixDefinitionIds.length > 0) {
      const affixId = lot.affix?.definitionId
        ?.replace(/_(pre|suf|aff)$/g, '')
        .replace(/_(pre|suf|aff)_?/g, '')
      if (!affixId || !filters.affixDefinitionIds.includes(affixId)) return false

      if (filters.affixQualities.length > 0) {
        if (!filters.affixQualities.includes(lot.affix?.quality ?? 0)) return false
      }
    }

    return true
  })

  // Фильтрация по поисковому запросу
  if (searchQuery.value && typeof searchQuery.value === 'string') {
    const query = searchQuery.value.toLowerCase()
    filteredLots = filteredLots.filter(lot => {
      const prefixName = lot.prefix?.name?.toLowerCase() || ''
      const suffixName = lot.suffix?.name?.toLowerCase() || ''
      const affixName = lot.affix?.name?.toLowerCase() || ''

      return prefixName.includes(query) || suffixName.includes(query) || affixName.includes(query)
    })
  } else if (searchQuery.value && typeof searchQuery.value === 'object' && searchQuery.value.name) {
    // Если выбрано из автокомплита
    const query = searchQuery.value.name.toLowerCase()
    filteredLots = filteredLots.filter(lot => {
      const prefixName = lot.prefix?.name?.toLowerCase() || ''
      const suffixName = lot.suffix?.name?.toLowerCase() || ''
      const affixName = lot.affix?.name?.toLowerCase() || ''

      return prefixName.includes(query) || suffixName.includes(query) || affixName.includes(query)
    })
  }

  return filteredLots.map(l => {
    const price = l.information?.price ?? 0
    const buyout = l.buyoutPrice ?? 0
    const salesCount = l.information.salesCount ?? 0
    const liquidityPerDay = l.information.liquidityPerDay ?? 0
    const affixPrice = l.information.affixPrice ?? 0
    const affixLiquidityPerDay = l.information.affixLiquidityPerDay ?? 0
    const affixSalesCount = l.information.affixSalesCount ?? 0
    const AC = l.information.AC
    const BC = l.information.BC
    const ACPrice = l.information.AC?.avgPrice
    const BCPrice = l.information.BC?.avgPrice
    const ACSalesCount = l.information.AC?.salesCount ?? 0
    const BCSalesCount = l.information.BC?.salesCount ?? 0

    // const affix = l?.additional?.attributes?.find(i => i.type === 2)
    // const statsRandom = affix?.statsRandom
    // const definitionId = affix?.definitionId

    // Расчет рыночной цены с учетом всех компонентов
    let marketPriceComponents = []
    let totalWeight = 0

    // Базовые веса для каждого типа данных
    const affixWeight = Math.min(affixSalesCount / 20, 1) // вес до 1.0
    const acWeight = ACSalesCount > 10 ? Math.min(ACSalesCount / 30, 1.2) : 0 // вес до 1.2
    const bcWeight = BCSalesCount > 10 ? Math.min(BCSalesCount / 30, 1.2) : 0 // вес до 1.2

    // Добавляем только если есть достаточные продажи
    if (affixPrice && affixSalesCount > 0) {
      marketPriceComponents.push({ price: affixPrice, weight: affixWeight })
      totalWeight += affixWeight
    }

    if (ACPrice && ACSalesCount > 10) {
      marketPriceComponents.push({ price: ACPrice, weight: acWeight })
      totalWeight += acWeight
    }

    if (BCPrice && BCSalesCount > 10) {
      marketPriceComponents.push({ price: BCPrice, weight: bcWeight })
      totalWeight += bcWeight
    }

    // Взвешенная средняя цена
    const weightedMarketPrice =
      marketPriceComponents.reduce((sum, comp) => sum + comp.price * comp.weight, 0) / totalWeight

    // Расчет дисперсии и коэффициента вариации для оценки надежности
    const validPrices = marketPriceComponents.map(comp => comp.price)
    const priceVariance =
      validPrices.length > 1
        ? validPrices.reduce((sum, price) => sum + Math.pow(price - weightedMarketPrice, 2), 0) /
          validPrices.length
        : 0
    const priceStdDev = Math.sqrt(priceVariance)
    const coefficientOfVariation =
      weightedMarketPrice > 0 ? (priceStdDev / weightedMarketPrice) * 100 : 0

    // учитываем комиссию 5% при продаже
    const netMarketPrice = weightedMarketPrice * 0.95
    const profit = netMarketPrice - buyout

    // Формула расчета score с балансом ЛИКВИДНОСТЬ + ЦЕНА + ПРОФИТ + НАДЕЖНОСТЬ
    // salesScore - умеренный бонус за продажи (0=500, 1-2=800, 3-5=1200, 6+=100*кол-во)
    // liquidityScore - главный фактор ликвидности (×200)
    // profitScore - профит важен (положительный ×1.5, отрицательный ×2)
    // affixLiquidityScore - ликвидность аффикса (×50)
    // optimalPriceBonus - оптимальная цена покупки (≤200к=1500, ≤500к=2000, ≤1млн=1000, ≤2млн=500)
    // goodDataBonus - бонус за стабильные данные (до 1200)
    // stabilityScore - стабильность цен (-500 до +400)
    // reliabilityScore - надежность данных (0 до 1500) - НОВЫЙ БОНУС
    // balanceMultiplier - мультипликатор за баланс (1.0 до 1.3)

    // Базовые показатели - баланс ликвидности, цены и профита
    const salesScore =
      salesCount === 0 ? 500 : salesCount <= 2 ? 800 : salesCount <= 5 ? 1200 : salesCount * 100 // умеренный бонус за продажи
    const liquidityScore = liquidityPerDay * 200 // высокий вес ликвидности (главный фактор)
    const profitScore = profit * 0.0001
    const affixLiquidityScore = affixLiquidityPerDay * 50 // ликвидность аффикса

    // Бонус за оптимальную цену покупки (не слишком дешево, не дорого)
    const optimalPriceBonus =
      buyout <= 200000
        ? 1500 // очень выгодно
        : buyout <= 500000
        ? 2000 // выгодно
        : buyout <= 1000000
        ? 500 // приемлемо
        : buyout <= 2000000
        ? 200
        : 0 // дорого

    // Бонус за хорошие данные (стабильная статистика)
    const goodDataBonus =
      (ACSalesCount >= 5 ? 600 : 0) +
      (BCSalesCount >= 5 ? 600 : 0) +
      affixSalesCount * 100 +
      (ACSalesCount >= 5 && BCSalesCount >= 5 ? 1000 : 0) // бонус за хорошие данные по обоим

    // Бонус за стабильность цен с увеличенными значениями
    const stabilityScore =
      coefficientOfVariation > 25
        ? -500
        : coefficientOfVariation > 15
        ? -200
        : coefficientOfVariation < 8
        ? 400
        : coefficientOfVariation < 12
        ? 150
        : 0

    // Бонус за надежность данных - чем ниже coefficientOfVariation, тем выше надежность
    const reliabilityScore =
      coefficientOfVariation < 5
        ? 1500 // очень надежные данные
        : coefficientOfVariation < 8
        ? 1000 // надежные данные
        : coefficientOfVariation < 12
        ? 500 // приемлемые данные
        : coefficientOfVariation < 20
        ? 200 // средние данные
        : 0 // ненадежные данные

    // Мультипликатор за баланс показателей
    const balanceMultiplier =
      liquidityPerDay >= 5 && profit > 50000 && buyout <= 500000
        ? 1.3 // идеальный баланс
        : liquidityPerDay >= 3 && profit > 0 && buyout <= 1000000
        ? 1.2 // хороший баланс
        : liquidityPerDay >= 1 && buyout <= 2000000
        ? 1.1 // приемлемый баланс
        : 1

    let score =
      (salesScore +
        liquidityScore +
        profitScore +
        affixLiquidityScore +
        optimalPriceBonus +
        goodDataBonus +
        stabilityScore +
        reliabilityScore) *
      balanceMultiplier

    // Условия надежности данных (для балансированной формулы)
    const hasReliableData =
      affixLiquidityPerDay >= 0.5 || // минимальная ликвидность
      ACSalesCount >= 3 ||
      BCSalesCount >= 3 || // базовые данные по компонентам
      totalWeight >= 0.5 // минимальный вес данных

    // Если профит слишком отрицательный или совсем нет данных, обнуляем score
    if (!hasReliableData || profit < 200000) {
      score = 0
    }

    return {
      buyoutPrice: buyout,
      marketPrice: weightedMarketPrice, // обновлено на взвешенную цену
      netMarketPrice, // добавлена чистая цена после комиссии
      profit,
      price,
      salesCount,
      liquidityPerDay,
      affixPrice,
      affixLiquidityPerDay,
      affixSalesCount,
      score,
      // Новые метрики надежности
      priceVariance,
      priceStdDev,
      coefficientOfVariation,
      hasReliableData,
      // suffixQualityNumber: statsRandom,
      prefix: l.prefix,
      suffix: l.suffix,
      affix: l.affix,
      AC: {
        ...AC,
        avgPrice: ACPrice,
        salesCount: ACSalesCount
      },
      BC: {
        ...BC,
        avgPrice: BCPrice,
        salesCount: BCSalesCount
      }
    }
  })
})

function formatPrice(n: number) {
  return n?.toLocaleString()
}

function profitClass(v: number) {
  if (v > 0) return 'profit'
  if (v < 0) return 'loss'
  return ''
}

const getQuality = (quality: number) => {
  return QUALITY_LIST.find(q => q.value === quality)
}

/* копирование */

const toast = ref()
const searchQuery = ref('')
const filteredSuggestions = ref([])

/* Фильтры */
const filters = reactive({
  prefixDefinitionIds: [],
  suffixDefinitionIds: [],
  affixDefinitionIds: [],
  prefixQualities: [],
  suffixQualities: [],
  affixQualities: []
})

const qualityOptions = QUALITY_LIST

const toggleQuality = (key, value) => {
  const arr = filters[key]
  const i = arr.indexOf(value)
  i > -1 ? arr.splice(i, 1) : arr.push(value)
}

// Функция для получения parameterRu по ID
const getParameterRu = (id: string, slot: string) => {
  // Очищаем ID от суффиксов если они есть
  const cleanId = id.replace(/_(pre|suf|aff)$/, '')
  const stat = MODULE_STATS.find(m => m.id === cleanId && m.slots.includes(slot))
  return stat?.parameterRu || ''
}

/* Options */
const prefixOptions = computed(() =>
  MODULE_STATS.filter(m => m.slots.includes('prefix')).map(m => ({
    label: m.nameRu,
    value: m.id
  }))
)

const suffixOptions = computed(() =>
  MODULE_STATS.filter(m => m.slots.includes('suffix')).map(m => ({
    label: m.nameRu,
    value: m.id
  }))
)

const affixOptions = computed(() =>
  MODULE_STATS.filter(m => m.slots.includes('affix')).map(m => ({
    label: m.nameRu,
    value: m.id
  }))
)

const copyingNotification = (text: string) => {
  toast.value?.add({
    severity: 'info',
    summary: 'Скопировано',
    detail: text,
    life: 2000
  })
}

const copyToClipboard = (text: string, type?: string) => {
  let copyValue = text

  // Если это цена, вычитаем 1 рубль
  if (type === 'price') {
    const numValue = parseFloat(text.replace(/[^\d.-]/g, ''))
    if (!isNaN(numValue)) {
      copyValue = (numValue - 1).toString()
    }
  }

  copyingNotification(copyValue)

  copyText(copyValue, undefined, (error: Error) => {
    if (error) console.error('Copy error:', error)
  })
}

const goToModuleHistory = (moduleData: any) => {
  const query: any = {}

  // Функция для очистки ID от суффиксов
  const cleanDefinitionId = (definitionId: string) => {
    return definitionId?.replace(/_(pre|suf|aff)$/g, '').replace(/_(pre|suf|aff)_?/g, '')
  }

  // Добавляем префикс если есть
  if (moduleData.prefix?.definitionId) {
    query.prefix = cleanDefinitionId(moduleData.prefix.definitionId)
    if (moduleData.prefix.quality !== undefined) {
      query.prefixQualities = moduleData.prefix.quality
    }
  }

  // Добавляем суффикс если есть
  if (moduleData.suffix?.definitionId) {
    query.suffix = cleanDefinitionId(moduleData.suffix.definitionId)
    if (moduleData.suffix.quality !== undefined) {
      query.suffixQualities = moduleData.suffix.quality
    }
  }

  // Добавляем аффикс если есть
  if (moduleData.affix?.definitionId) {
    query.affix = cleanDefinitionId(moduleData.affix.definitionId)
    if (moduleData.affix.quality !== undefined) {
      query.affixQualities = moduleData.affix.quality
    }
  }

  // Строим URL с query-параметрами
  const queryString = new URLSearchParams(query).toString()
  const url = `/module${queryString ? '?' + queryString : ''}`

  // Открываем в новой вкладке
  window.open(url, '_blank')
}

/* Поиск атрибутов */
const searchAttributes = event => {
  if (!event || !event.query) {
    filteredSuggestions.value = []
    return
  }

  const query = event.query.toLowerCase()
  if (!query || query.length < 2) {
    filteredSuggestions.value = []
    return
  }

  // Ищем по всем названиям атрибутов
  const allAttributes = (MODULE_STATS || [])
    .map(stat => ({
      name: stat.nameRu || '',
      id: stat.id || '',
      slot: stat.slots && stat.slots[0] ? stat.slots[0] : 'unknown'
    }))
    .filter(attr => attr.name && attr.id) // фильтруем пустые значения

  const matches = allAttributes.filter(attr => attr.name.toLowerCase().includes(query))

  filteredSuggestions.value = matches.slice(0, 10) // ограничиваем количество предложений
}

const handleAttributeSelect = event => {
  if (event.value && event.value.name) {
    searchQuery.value = event.value.name
  }
}

const clearSearch = () => {
  searchQuery.value = ''
  filteredSuggestions.value = []
}
// Директива v-tooltip уже доступна глобально через PrimeVue
</script>

<style scoped>
.title {
  font-size: 20px;
  margin-bottom: 14px;
}

.table {
  background: #111;
}

.clickable-price {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  padding: 2px 4px;
}

.clickable-price:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.attrs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  cursor: pointer;
}

.profit {
  color: #4ade80;
  font-weight: 600;
}

.loss {
  color: #f87171;
  font-weight: 600;
}

.history-button {
  background-color: #60a5fa !important;
  border-color: #60a5fa !important;
  color: white !important;
}

.history-button:hover {
  background-color: #3b82f6 !important;
  border-color: #3b82f6 !important;
}

/* Стили для фильтров */
.filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.filter-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #1f2937;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.filter-block label {
  color: #e5e7eb;
  font-size: 14px;
  font-weight: 500;
}

.quality-buttons {
  display: flex;
  gap: 6px;
}

.quality-buttons button {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: transform 0.15s ease;
}

.quality-buttons button.active {
  transform: scale(1.25);
  border: 1px solid white;
}

.search-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* Стили для поиска */
.search-autocomplete {
  flex: 1;
}

.search-autocomplete :deep(.p-autocomplete-input) {
  background: #111;
  border: 1px solid #333;
  padding: 8px 12px;
  color: white;
  border-radius: 6px;
  width: 100%;
  font-size: 14px;
}

.search-autocomplete :deep(.p-autocomplete-input:focus) {
  outline: none;
  border-color: #60a5fa;
}

.search-autocomplete :deep(.p-autocomplete-panel) {
  background: #1f2937;
  border: 1px solid #374151;
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
}

.search-autocomplete :deep(.p-autocomplete-item) {
  color: #e5e7eb;
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.search-autocomplete :deep(.p-autocomplete-item:hover) {
  background: #374151;
}

.search-autocomplete :deep(.p-autocomplete-item:last-child) {
  border-bottom: none;
}

.clear-button {
  background: #ef4444;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;
  min-width: 32px;
}

.clear-button:hover {
  background: #dc2626;
}
</style>
```
